import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

export type ModuleDef = {
    code: string;
    parent: string | null;
    routes: string[];
    perm_prefixes: string[];
    actions: string[]; // subset of ["view","edit","delete"]
    account_critical: boolean;
    platform_level: boolean;
};

export type AccessMap = Record<string, Record<string, boolean>>;

/**
 * Renders the module tree with view/edit/delete toggles per row.
 *
 * * Account-critical modules render as locked "always on" rows.
 * * A child row is disabled when its parent's `view` is off (cascade).
 * * Toggling `view` off cascades down to disable all children view/edit/delete.
 * * Enabling a parent never auto-enables children.
 */
export function AccessPolicyEditor({
    modules,
    value,
    onChange,
    disabled = false,
    testIdPrefix = "policy",
}: {
    modules: ModuleDef[];
    value: AccessMap;
    onChange: (next: AccessMap) => void;
    disabled?: boolean;
    testIdPrefix?: string;
}) {
    const { t } = useTranslation();

    // Group by root module.
    const roots = useMemo(() => modules.filter(m => !m.parent && !m.account_critical), [modules]);
    const childrenOf = useMemo(() => {
        const idx: Record<string, ModuleDef[]> = {};
        for (const m of modules) if (m.parent) (idx[m.parent] = idx[m.parent] || []).push(m);
        return idx;
    }, [modules]);

    const parentView = (code: string): boolean => {
        const m = modules.find(x => x.code === code);
        if (!m || !m.parent) return true;
        return !!value[m.parent]?.view;
    };

    const setCell = (code: string, action: string, on: boolean) => {
        if (disabled) return;
        const m = modules.find(x => x.code === code);
        if (!m) return;
        const next: AccessMap = { ...value };
        next[code] = { ...(next[code] || {}), [action]: on };
        // Cascade OFF only.
        if (action === "view" && !on) {
            for (const child of modules.filter(x => x.parent === code)) {
                next[child.code] = Object.fromEntries(child.actions.map(a => [a, false]));
            }
        }
        onChange(next);
    };

    const setRowAll = (code: string, on: boolean) => {
        const m = modules.find(x => x.code === code);
        if (!m) return;
        const next: AccessMap = { ...value };
        next[code] = Object.fromEntries(m.actions.map(a => [a, on]));
        if (!on) {
            for (const child of modules.filter(x => x.parent === code)) {
                next[child.code] = Object.fromEntries(child.actions.map(a => [a, false]));
            }
        }
        onChange(next);
    };

    return (
        <div className="rounded-md border overflow-hidden" style={{ borderColor: "var(--border)" }}
            data-testid={`${testIdPrefix}-editor`}>
            <table className="w-full text-sm">
                <thead style={{ background: "var(--input)" }}>
                    <tr className="text-left">
                        <th className="px-3 py-2">{t("platform.access.module")}</th>
                        <th className="px-3 py-2 text-center w-24">{t("platform.access.action.view")}</th>
                        <th className="px-3 py-2 text-center w-24">{t("platform.access.action.edit")}</th>
                        <th className="px-3 py-2 text-center w-24">{t("platform.access.action.delete")}</th>
                        <th className="px-3 py-2 text-center w-28">{t("platform.access.row_all")}</th>
                    </tr>
                </thead>
                <tbody>
                    {roots.map(root => (
                        <ModuleRows key={root.code} root={root} childrenOf={childrenOf[root.code] || []}
                            value={value} setCell={setCell} setRowAll={setRowAll}
                            parentView={parentView} disabled={disabled}
                            testIdPrefix={testIdPrefix} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ModuleRows({ root, childrenOf, value, setCell, setRowAll, parentView, disabled, testIdPrefix }: {
    root: ModuleDef;
    childrenOf: ModuleDef[];
    value: AccessMap;
    setCell: (code: string, action: string, on: boolean) => void;
    setRowAll: (code: string, on: boolean) => void;
    parentView: (code: string) => boolean;
    disabled: boolean;
    testIdPrefix: string;
}) {
    const { t } = useTranslation();
    return (
        <>
            <Row m={root} value={value} setCell={setCell} setRowAll={setRowAll}
                blocked={disabled} depth={0} testIdPrefix={testIdPrefix} t={t} />
            {childrenOf.map(child => (
                <Row key={child.code} m={child} value={value} setCell={setCell} setRowAll={setRowAll}
                    blocked={disabled || !parentView(child.code)} depth={1}
                    testIdPrefix={testIdPrefix} t={t} />
            ))}
        </>
    );
}

function Row({ m, value, setCell, setRowAll, blocked, depth, testIdPrefix, t }: {
    m: ModuleDef; value: AccessMap;
    setCell: (code: string, action: string, on: boolean) => void;
    setRowAll: (code: string, on: boolean) => void;
    blocked: boolean; depth: number; testIdPrefix: string; t: any;
}) {
    const label = t(`module.${m.code}` as any, { defaultValue: m.code });
    const cur = value[m.code] || {};
    const allOn = m.actions.every(a => cur[a]);
    return (
        <tr className="border-t" style={{ borderColor: "var(--border)",
                                            opacity: blocked ? 0.5 : 1 }}
            data-testid={`${testIdPrefix}-row-${m.code}`}>
            <td className="px-3 py-2 font-medium" style={{ paddingLeft: 12 + depth * 20 }}>
                {label}
                <span className="ml-2 text-[10px] font-mono px-1 rounded"
                      style={{ background: "var(--input)", color: "var(--text-muted)" }}>{m.code}</span>
            </td>
            {(["view", "edit", "delete"] as const).map(action => {
                const supported = m.actions.includes(action);
                if (!supported) return <td key={action} className="px-3 py-2 text-center text-[11px]" style={{ color: "var(--text-muted)" }}>—</td>;
                const on = !!cur[action];
                return (
                    <td key={action} className="px-3 py-2 text-center">
                        <button type="button" onClick={() => setCell(m.code, action, !on)}
                            disabled={blocked}
                            data-testid={`${testIdPrefix}-${m.code}-${action}`}
                            className="inline-flex items-center justify-center rounded-md border w-16 py-1 text-xs"
                            style={{
                                background: on ? "var(--success-bg)" : "var(--input)",
                                color: on ? "var(--success-fg)" : "var(--text-muted)",
                                borderColor: "var(--border)",
                            }}>
                            {on ? <Check size={12} className="mr-1" /> : null}
                            {on ? t("common.on") : t("common.off")}
                        </button>
                    </td>
                );
            })}
            <td className="px-3 py-2 text-center">
                <button type="button" onClick={() => setRowAll(m.code, !allOn)} disabled={blocked}
                    data-testid={`${testIdPrefix}-${m.code}-all`}
                    className="text-xs rounded-md border px-2 py-1"
                    style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                    {allOn ? t("platform.access.turn_off_all") : t("platform.access.turn_on_all")}
                </button>
            </td>
        </tr>
    );
}

export function anyEnabledNonCritical(policy: AccessMap, modules: ModuleDef[]): boolean {
    for (const m of modules) {
        if (m.account_critical) continue;
        for (const a of m.actions) if (policy[m.code]?.[a]) return true;
    }
    return false;
}
